import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { ArrowLeftOutlined, SbTooltip } from "uiCore";
import { SbTabs } from "uiCore";
import SbButton from "uiCore/SbButton/SbButton";
import SbJdCreationDrawerV2 from "Components/SbJdCreationDrawerV2/SbJdCreationDrawerV2";
import SbBulkJdUpload from "Pages/SbBulkJdUpload/SbBulkJdUpload";
import SbAdminAllJobsView from "Pages/SbAdminAllJobsView/SbAdminAllJobsView";
import { JD_ACTION } from "constants/jobs.constants";
import { DEEP_LINK_ACTIONS } from "Utils/deepLinks";
import useDeepLinkAction from "Hooks/useDeepLinkAction";
import { useSelector, useDispatch } from "react-redux";
import { showSbConfirm } from "Utils/sbModal";
import { hideToast } from "Store/actions/profileListActions";
import ToasterModal from "Components/SbErrorToasterComponent/SbErrorToaster";

import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  TitleRow,
  BackButton,
  TabsRow,
  ContentSection,
  DrawerClosedSection,
  PlaceholderText,
  AllJobsSection,
} from "./AdminSetupUploadJd.styles";

const { Title } = Typography;

const TOOLTIP_SINGLE_UPLOAD = "Upload one JD document at a time with a structured form.";
const TOOLTIP_BULK_UPLOAD = "Upload up to 50 JDs at once using an Excel template.";
const TOOLTIP_ALL_JOBS = "View all job codes in your organisation (read-only).";

const UPLOAD_JD_TABS = [
  {
    key: "single-upload",
    label: "Single Upload",
    tooltip: TOOLTIP_SINGLE_UPLOAD,
  },
  {
    key: "bulk-upload",
    label: "Bulk Upload",
    tooltip: TOOLTIP_BULK_UPLOAD,
  },
  {
    key: "all-jobs",
    label: "All Jobs",
    tooltip: TOOLTIP_ALL_JOBS,
  },
];

const AdminSetupUploadJd = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show, message, type: toastType } = useSelector((s) => s.profileList.toast);
  const [activeTab, setActiveTab] = useState("single-upload");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isBulkDirty, setIsBulkDirty] = useState(false);
  const [allJobsRefreshTrigger, setAllJobsRefreshTrigger] = useState(0);

  // ── Deep links: ?action=upload-jd-single | upload-jd-bulk ──────────────────
  // Single Upload, Bulk Upload and All Jobs are tabs on ONE route, so a link to
  // the bare route always lands on Single Upload — wrong for anyone who asked
  // about bulk upload. These select the tab the answer was actually about.
  // No extra permission gate: the route itself already requires
  // admin_setup-upload_external_jd:create via EntitlementGuard, and these only
  // switch between tabs that entitlement already grants.
  useDeepLinkAction({
    action: DEEP_LINK_ACTIONS.UPLOAD_JD_SINGLE,
    onAllowed: () => setActiveTab("single-upload"),
  });
  useDeepLinkAction({
    action: DEEP_LINK_ACTIONS.UPLOAD_JD_BULK,
    onAllowed: () => setActiveTab("bulk-upload"),
  });

  const tabItems = UPLOAD_JD_TABS.map((tab) => ({
    key: tab.key,
    label: (
      <SbTooltip content={tab.tooltip}>
        <span>{tab.label}</span>
      </SbTooltip>
    ),
  }));

  const handleBulkDirtyChange = useCallback((dirty) => {
    setIsBulkDirty(dirty);
  }, []);

  const handleBulkSubmitSuccess = useCallback(() => {
    setAllJobsRefreshTrigger((n) => n + 1);
    setActiveTab("all-jobs");
    setIsDrawerOpen(false);
  }, []);

  const handleSingleSubmitSuccess = useCallback(() => {
    setAllJobsRefreshTrigger((n) => n + 1);
    setActiveTab("all-jobs");
    setIsDrawerOpen(false);
  }, []);

  const switchTab = (key) => {
    setActiveTab(key);
    setIsDrawerOpen(false);
  };

  const handleTabChange = (key) => {
    if (activeTab === "bulk-upload" && isBulkDirty) {
      showSbConfirm({
        title: "Unsaved data",
        content: "You have unsaved rows. Switching tabs will discard your changes. Continue?",
        okText: "Discard & Switch",
        cancelText: "Stay",
        onOk: () => switchTab(key),
      });
      return;
    }
    switchTab(key);
  };

  const handleBack = () => {
    if (activeTab === "bulk-upload" && isBulkDirty) {
      showSbConfirm({
        title: "Unsaved data",
        content: "You have unsaved rows. Leaving this page will discard your changes.",
        okText: "Leave anyway",
        cancelText: "Stay",
        onOk: () => navigate("/admin-setup"),
      });
      return;
    }
    navigate("/admin-setup");
  };

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <TitleRow>
            <BackButton onClick={handleBack} type="button">
              <ArrowLeftOutlined />
            </BackButton>
            <Title level={3}>Upload External JD</Title>
          </TitleRow>
        </HeaderTopRow>

        <TabsRow>
          <SbTabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
          />
        </TabsRow>
      </HeaderSection>

      <ContentSection>
        {activeTab === "single-upload" && (
          <>
            {!isDrawerOpen && (
              <DrawerClosedSection>
                <PlaceholderText>Upload a single external job description.</PlaceholderText>
                <SbButton buttonType="primary" onClick={() => setIsDrawerOpen(true)}>
                  Single Upload
                </SbButton>
              </DrawerClosedSection>
            )}
            <SbJdCreationDrawerV2
              jdActionSelection={JD_ACTION.UPLOAD}
              isDrawerOpen={isDrawerOpen}
              toggleDrawer={() => setIsDrawerOpen(false)}
              setPendingJdId={() => {}}
              onUploadSuccess={handleSingleSubmitSuccess}
            />
          </>
        )}

        {activeTab === "bulk-upload" && (
          <SbBulkJdUpload embedded onDirtyChange={handleBulkDirtyChange} onSubmitSuccess={handleBulkSubmitSuccess} />
        )}

        {activeTab === "all-jobs" && (
          <AllJobsSection>
            <SbAdminAllJobsView embedded refreshTrigger={allJobsRefreshTrigger} />
          </AllJobsSection>
        )}
      </ContentSection>

      <ToasterModal
        message={message}
        show={show}
        type={toastType}
        onClose={() => dispatch(hideToast())}
      />
    </PageContainer>
  );
};

export default AdminSetupUploadJd;
